// src/components/Navbar.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Paper,
  Typography,
  Button,
  useTheme,
  useMediaQuery
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useLocation } from "react-router-dom";

const NAV = {
  bg: "linear-gradient(90deg, #FFFDF8, #FFF2DD)",
  border: "#EADFCB",
  text: "#1A1A1A",
  subText: "#6B5E4A",
  accent: "#F5C400",
  hover: "#FFEFC4"
};

// 🔹 Map route → title
const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/submissions": "Submissions",
  "/settings/users": "Users",
  "/settings": "Settings"
};

const Navbar = ({ handleDrawerToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const [openMenu, setOpenMenu] = useState(false);
  const ref = useRef(null);

  const pageTitle =
    PAGE_TITLES[location.pathname] || "Dashboard";

  const userEmail = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("userName")) || "";
    } catch {
      return "";
    }
  })();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: NAV.bg,
        borderBottom: `1px solid ${NAV.border}`,
        height: 64,
        justifyContent: "center",
        width: { md: "calc(100% - 240px)" },
        ml: { md: "240px" }
      }}
    >
      <Toolbar sx={{ minHeight: "64px !important", px: 2 }}>
        {/* LEFT: MENU + TITLE */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ color: NAV.text }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: NAV.text,
              whiteSpace: "nowrap"
            }}
          >
            {pageTitle}
          </Typography>
        </Box>

        {/* SPACER */}
        <Box sx={{ flexGrow: 1 }} />

        {/* RIGHT: USER */}
        <Box ref={ref} sx={{ position: "relative" }}>
          <Avatar
            onClick={() => setOpenMenu(!openMenu)}
            sx={{
              bgcolor: "#F3C26B",
              color: "#000",
              fontWeight: 700,
              cursor: "pointer",
              width: 42,
              height: 42,
              "&:hover": {
                boxShadow: "0 0 0 5px rgba(245,196,0,0.25)"
              }
            }}
          >
            {userEmail?.charAt(0)?.toUpperCase() || "U"}
          </Avatar>

          {/* LOGOUT MENU */}
          {openMenu && (
            <Paper
              elevation={10}
              sx={{
                position: "absolute",
                top: "120%",
                right: 0,
                width: 260,
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${NAV.border}`,
                background: "#FFFDF8",
                zIndex: 3000
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 13, color: NAV.subText }}>
                  Signed in as
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "NAV.text",
                    wordBreak: "break-all"
                  }}
                >
                  {userEmail}
                </Typography>
              </Box>

              <Button
                fullWidth
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
                sx={{
                  py: 1.6,
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#000",
                  backgroundColor: "#F3C26B",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#F3C26B"
                  }
                }}
              >
                Logout
              </Button>
            </Paper>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
